import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, LoadingSpinner, DataTable, useToastActions } from '@airclou/ui';
import { ArrowLeft, Users, Briefcase, Pencil, Trash2, UserPlus, X } from 'lucide-react';
import { getGroupDetails, removeGroupMember, deleteGroup } from '@/api/groups';
import type { GroupDetail, GroupMember } from '@/api/groups';
import { getCompany, type Company } from '@/api/companies';
import { EditGroupModal } from '../../components/hierarchy/EditGroupModal';
import { DeleteGroupModal } from '../../components/hierarchy/DeleteGroupModal';
import { AddMembersToGroupModal } from '../../components/hierarchy/AddMembersToGroupModal';
import { isRecordGone } from '@/api/errors';
import { ErrorState } from '../../components/state/ErrorState';
import { RefreshErrorBanner } from '../../components/state/RefreshErrorBanner';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../../components/state/RoleUnknownNotice';
import { ValueSkeleton } from '../../components/state/ValueSkeleton';

export function GroupDetailsPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { id: companyId, groupId } = useParams<{ id: string; groupId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToastActions();
  const { user } = useAuth();

  // Edit, Delete, Add members and Remove member are all
  // `TENANT_ROLE_SETS.MANAGE` on the API as of 2026-09-13, where group
  // management had no gate at all before. This page is reachable to a viewer by
  // design — the group name on the company's Groups tab is a link, and reading
  // stays open to any member — which is exactly why the writes have to be gated
  // here too rather than only on the tab that links in. `canManageOrg` mirrors
  // the role set; see the same note in CompanyGroupsTab.
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The group is gone — deleted, or not this company's. A retry cannot bring it
  // back, so the page has nothing left to show even while it holds the rows it
  // loaded. Only a successful load clears it.
  const [gone, setGone] = useState(false);
  const [activeTab, setActiveTab] = useState('members');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [memberSorting, setMemberSorting] = useState<any>([]);

  // Which load is the current one, and which group the page is showing. The route
  // stays mounted across a group switch, so an answer for the group we left can
  // still be in flight. Both have to agree: the id alone lets a refresh started
  // after the switch win over the live load, and the sequence alone lets such a
  // caller claim the newest number and then fail its own id check, orphaning the
  // live load with `loading` left set. The ref is written from the id-change
  // effect, not during render, so a render React abandons publishes nothing.
  const loadSeq = useRef(0);
  const shownId = useRef(`${companyId}:${groupId}`);

  const stillShowing = (loadId: string) => loadId === shownId.current;
  const isCurrent = (seq: number, loadId: string) =>
    seq === loadSeq.current && stillShowing(loadId);

  const loadData = useCallback(async () => {
    if (!companyId || !groupId) return;

    const loadId = `${companyId}:${groupId}`;
    // Bail before claiming a sequence number: a stale caller that took the newest
    // one and then failed its own id check would orphan the live load.
    if (!stillShowing(loadId)) return;

    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      // `error` is cleared on success rather than here, so the retry affordance
      // the operator just clicked is still on screen to show it is working.

      const groupRes = await getGroupDetails(companyId, groupId);
      if (!isCurrent(seq, loadId)) return;

      setGroup(groupRes.group);
      setGone(false);
      setError(null);

      // The company only labels the back button, so its failure must not take
      // down a page whose own resource loaded. Deliberately not awaited.
      getCompany(companyId)
        .then(companyRes => {
          if (isCurrent(seq, loadId)) setCompany(companyRes.company);
        })
        .catch(err => {
          // Logged rather than surfaced: the operator loses a name on a button,
          // not access to anything.
          console.error('Failed to load company for back link:', err);
          if (isCurrent(seq, loadId)) setCompany(null);
        });
    } catch (err: any) {
      if (!isCurrent(seq, loadId)) return;
      // Latches: only a successful load clears it, so a blip on the retry cannot
      // put a deleted group — and its Edit and Delete buttons — back.
      if (isRecordGone(err)) setGone(true);
      setError(err.message || t('groups.failedToLoadDetails'));
    } finally {
      if (isCurrent(seq, loadId)) setLoading(false);
    }
  }, [companyId, groupId]);

  useEffect(() => {
    shownId.current = `${companyId}:${groupId}`;
    // A different group under the same mounted route. Drop the previous one's
    // data first: nothing from group A may render — or be edited — under group
    // B's URL, and the Edit and Delete modals have to go with it.
    setGroup(null);
    setCompany(null);
    setGone(false);
    setError(null);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsAddMembersModalOpen(false);
    // Describes *this page* working, so it must not carry over.
    setRemovingMemberId(null);
    loadData();
  }, [loadData]);

  const handleRemoveMember = async (userId: string) => {
    if (!companyId || !groupId) return;
    setRemovingMemberId(userId);
    try {
      const loadId = `${companyId}:${groupId}`;
      await removeGroupMember(companyId, groupId, userId);
      success(t('groups.memberRemovedFromGroup'));
      // This handler's `loadData` is the closure from the render that showed
      // `loadId`, so after a switch it bails on its own id check anyway — this
      // just stops the pointless call. The load-bearing guard is in `loadData`.
      if (stillShowing(loadId)) loadData();
    } catch (err: any) {
      showError(err.message || t('groups.failedToRemoveMember'));
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleDelete = async () => {
    if (!companyId || !groupId) return;
    try {
      await deleteGroup(companyId, groupId);
      success(t('groups.groupDeleted'));
      navigate(`/companies/${companyId}/groups`);
    } catch (err: any) {
      showError(err.message || t('groups.failedToDelete'));
    }
  };

  const memberColumns: any[] = [
    {
      id: 'name',
      header: t('common.member'),
      cell: (member: GroupMember) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-sm font-medium">
            {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div className="font-medium text-foreground">{member.user?.name}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{member.user?.email}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'added_at',
      header: t('common.added'),
      cell: (member: GroupMember) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(member.added_at, timezone)}
        </div>
      ),
    },
    // The column goes rather than emptying, so a read-only viewer does not get a
    // blank gutter with a blank header over it.
    ...(canManage ? [{
      id: 'actions',
      header: '',
      cell: (member: GroupMember) => (
        <div className="flex items-center justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveMember(member.user_id)}
            disabled={removingMemberId === member.user_id}
          >
            <X className="w-3.5 h-3.5 mr-1" />
            {t('common.remove')}
          </Button>
        </div>
      ),
    }] : []),
  ];

  // `gone` stands on its own; otherwise this is only for "there is no group to
  // show". The company is not in the condition: it labels the back button, and a
  // page whose own resource loaded must not be replaced because that label failed.
  // Keyed off `error` as well as `!loading` so the error state survives its own
  // retry instead of flashing the skeleton shell mid-flight.
  if (gone || (!group && (error || !loading))) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/companies/${companyId}/groups`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <ErrorState
          title={t('groups.failedToLoadDetails')}
          description={gone || !error ? t('groups.groupNotFound') : error}
          onRetry={loadData}
          isRetrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/companies/${companyId}/groups`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {company?.name ? t('workspaces.backToCompany', { name: company.name }) : t('workspaces.backToCompanyDefault')}
        </Button>

        <div className="flex items-start justify-between" aria-busy={!group}>
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-purple-100 dark:bg-purple-900/30">
              <Users className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              {group ? (
                <>
                  <h1 className="text-2xl font-bold text-foreground">{group.name}</h1>
                  {group.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{group.description}</p>
                  )}
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {t('common.created')} {formatDateInTz(group.created_at, timezone)}
                    {group.created_by_name && ` by ${group.created_by_name}`}
                  </div>
                </>
              ) : (
                // Mirrors the name and "Created" lines. The description is
                // optional, so there is no way to reserve its line in advance.
                <>
                  <div className="h-8 w-48 animate-pulse rounded bg-muted" />
                  <div className="mt-2 text-sm"><ValueSkeleton className="w-64" /></div>
                </>
              )}
            </div>
          </div>
          {/* Rendered from the start, disabled until there is a group to act on:
              buttons that appear late make the page feel like it is still loading. */}
          {canManage && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(true)} disabled={!group}>
                <Pencil className="w-4 h-4 mr-2" />
                {t('common.edit')}
              </Button>
              <Button variant="outline" onClick={() => setIsDeleteModalOpen(true)} disabled={!group}>
                <Trash2 className="w-4 h-4 mr-2" />
                {t('common.delete')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <RoleUnknownNotice />

      {/* A reload that failed while the page still holds a good row: keep the page
          and offer a retry. A "group is gone" answer took the early return above. */}
      {error && group && (
        <RefreshErrorBanner message={error} onRetry={loadData} isRetrying={loading} />
      )}

      {/* Tabs — the strip is part of the shell, so it renders immediately and each
          tab gates only its own data region. The counts wait for the rows:
          "Members (0)" on a group that has members reads as an answer. */}
      <Tabs
        key={`${companyId}:${groupId}`}
        tabs={[
          {
            id: 'members',
            label: group ? `${t('common.members')} (${group.members.length})` : t('common.members'),
            content: !group ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {t('common.members')} ({group.members.length})
                    </h2>
                  </div>
                  {canManage && (
                    <Button onClick={() => setIsAddMembersModalOpen(true)}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t('groups.addMembers')}
                    </Button>
                  )}
                </div>

                {group.members.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('groups.noMembersYet')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                      {t('groups.addCompanyUsers')}
                    </p>
                    {canManage && (
                      <Button onClick={() => setIsAddMembersModalOpen(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        {t('groups.addMembers')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <DataTable
                    data={group.members}
                    columns={memberColumns}
                    sorting={memberSorting}
                    onSortingChange={setMemberSorting}
                  />
                )}
              </div>
            ),
          },
          {
            id: 'workspaces',
            // `assignedWorkspaces` bakes the count into the string, so the
            // loading label falls back to the plain noun rather than claiming 0.
            label: group
              ? t('groups.assignedWorkspaces', { count: group.workspaces.length })
              : t('common.workspaces'),
            content: !group ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-foreground">
                  {t('groups.assignedWorkspaces', { count: group.workspaces.length })}
                </h2>

                {group.workspaces.length === 0 ? (
                  <div className="p-12 text-center">
                    <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('groups.noWorkspacesAssigned')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('groups.noWorkspacesAssignedDesc')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {group.workspaces.map((ws) => (
                      <div
                        key={ws.workspace_id}
                        className="flex items-center justify-between p-3 border border-border rounded-lg bg-card cursor-pointer hover:border-primary/40 transition-colors"
                        onClick={() => navigate(`/workspaces/${ws.workspace_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15 dark:bg-primary/25">
                            <Briefcase className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground text-sm">
                              {ws.workspace_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('common.assigned')} {formatDateInTz(ws.assigned_at, timezone)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
                            {t('common.assigned')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
          },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Modals */}
      {group && isEditModalOpen && (
        <EditGroupModal
          companyId={companyId!}
          group={{
            ...group,
            workspace_count: group.workspaces.length,
          }}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setIsEditModalOpen(false);
            loadData();
          }}
        />
      )}

      {group && isDeleteModalOpen && (
        <DeleteGroupModal
          group={{
            ...group,
            workspace_count: group.workspaces.length,
          }}
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
        />
      )}

      {group && isAddMembersModalOpen && (
        <AddMembersToGroupModal
          companyId={companyId!}
          groupId={groupId!}
          groupName={group.name}
          isOpen={isAddMembersModalOpen}
          onClose={() => setIsAddMembersModalOpen(false)}
          onSuccess={() => {
            setIsAddMembersModalOpen(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}
